import Head from 'next/head';
import Link from 'next/link';
import React, { FormEvent, useState } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import style from './sub.module.css';
import { Loader } from '../../components/Loader/Loader';
import { useLoader } from '../../hooks/useLoader';
import type { GetServerSidePropsContext } from 'next';
import { GreenButton } from '../../components/ButtonsCollection/GreenButton/GreenButton';
import styled from 'styled-components';
import { fetchFromClient } from '../../fetch';
import { PATH_PRIVACY } from '../../constants/spa-routes';
import Script from 'next/script';

interface SubProcessorsListPageProps {
  originUrl: string;
}

const EmailContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const NotifyContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '20px',
});

const NotifyWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  maxWidth: '350px',
  width: '100%',
  position: 'relative',
});
const SuccessMessage = styled.p({
  textAlign: 'center',
  color: 'green',
  position: 'absolute',
  bottom: '-35px',
  width: '100%',
});

export default function DpaPage({ originUrl }: SubProcessorsListPageProps) {
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const loading = useLoader();

  const validate = (value: string) => {
    return /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/.test(value);
  };

  const onBlurHandler = () => {
    const isValid = validate(email);
    setIsEmailValid(isValid);
    if (!isValid) {
      setEmailErrorText(email ? 'Invalid Email Format' : 'Required');
    } else {
      setEmailErrorText('');
    }
  };

  const submitForm = async (event: FormEvent) => {
    event.preventDefault();

    const isValid = validate(email);

    if (!email) {
      setIsEmailValid(isValid);
      setEmailErrorText('Required');
      return;
    }

    if (!isValid) {
      setIsEmailValid(isValid);
      setEmailErrorText('Invalid Email Format');

      return;
    }
    setEmailErrorText('');
    const response = await fetchFromClient('POST', '/notify-me-with-email', {
      email,
      name: `Sub processors: ${email}`,
      tags: ['Sub processors list'],
    });

    if (response instanceof Error) {
      return console.warn(response.message);
    } else {
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 2000);
    }
  };

  return (
    <>
      <Head>
        <title>CoSupport AI | Sub-Processors List</title>
        <meta key="subject" name="subject" content="CoSupport AI | Sub-Processors List"></meta>
        <meta
          key="description"
          name="description"
          content="CoSupport AI's Sub-Processors List: Transparent information about third-party vendors ensuring secure data processing."
        />
        <link
          key="canonical"
          rel="canonical"
          href={`${originUrl}${PATH_PRIVACY.SUB_PROCESSORS_LIST}`}
        />
        <meta key="ogtitle" property="og:title" content="CoSupport AI | Sub-Processors List"></meta>
        <meta key="ogtype" property="og:type" content="services" />
        <meta key="oglocale" property="og:locale" content="en_US" />
        <meta key="ogurl" property="og:url" content={`${originUrl}/sub-processors-list`} />
        <meta
          key="ogimage"
          property="og:image"
          content={`${originUrl}/img/open-graph/terms-page.png`}
        />
        <meta key="ogsitename" property="og:site_name" content={`${originUrl}/`} />
        <meta
          key="ogdescription"
          property="og:description"
          content="CoSupport AI's Sub-Processors List: Transparent information about third-party vendors ensuring secure data processing."
        />
        <meta key="viewport" name="viewport" content="initial-scale=1.0, width=device-width" />
        <Script
          id="googletagmanager3"
          key="googletagmanager3"
          strategy="beforeInteractive"
          src="https://www.googletagmanager.com/gtag/js?id=AW-11237889467"
        />
        <Script
          id="googletagmanager4"
          key="googletagmanager4"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-11237889467');
          `,
          }}
        />
        <Script
          id="googletagmanager5"
          key="googletagmanager5"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
            (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-WXHFTKL9');
          `,
          }}
        />
        <Script
          id="dealfront"
          key="dealfront"
          dangerouslySetInnerHTML={{
            __html: `(function(ss,ex){ window.ldfdr=window.ldfdr||function(){(ldfdr._q=ldfdr._q||[]).push([].slice.call(arguments));}; (function(d,s){ fs=d.getElementsByTagName(s)[0]; function ce(src){ var cs=d.createElement(s); cs.src=src; cs.async=1; fs.parentNode.insertBefore(cs,fs); }; ce('https://sc.lfeeder.com/lftracker_v1_'+ss+(ex?'_'+ex:'')+'.js'); })(document,'script'); })('JMvZ8gnv0VPa2pOd');`,
          }}
        />
        <Script
          id="apollo"
          key="apollo"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `!function(){var e,a;e=Math.random().toString(36).substring(7),(a=document.createElement("script")).src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+e,a.async=!0,a.defer=!0,a.onload=function(){window.trackingFunctions.onLoad({appId:"66bb4c503be771078edff8ab"})},document.head.appendChild(a)}();`,
          }}
        />
      </Head>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: 'fixed', top: '-100%', left: '-100%' }}
      >
        <svg id="button-right-arrow" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17 11.2422L1.31134e-07 11.2422L0 8.24219L17 8.24218L17 11.2422Z"
            fill="#05050D"
          />
          <path
            d="M9.24302 17.556L16.3141 10.4849C16.7046 10.0944 16.7046 9.46125 16.3141 9.07073L9.24302 1.99966"
            stroke="#05050D"
            strokeWidth="3"
          />
        </svg>
      </svg>
      {loading && <Loader />}
      <Header pathname={PATH_PRIVACY.SUB_PROCESSORS_LIST} />
      <main>
        <div className={style.container}>
          <div className={style.content}>
            <section className={style.content}>
              <h1 className="font-formula">CoSupport’ s Sub-processors List</h1>
              <h4>Effective date: May 15, 2024</h4>
              <p>
                To support delivery of our services, CoSupport and its affiliates and subsidiaries
                (collectively referred to as <b>“CoSupport”</b> or <b>“We”</b> or <b>“Our”</b> or{' '}
                <b>“Us”</b>) may engage and use data processors with access to certain personal data
                (each, a <b>“Sub-processors”</b>). This page provides important information about
                the identity, location and role of each Sub-processor. Terms used on this page but
                not defined have the meaning set forth in the{' '}
                <Link href={PATH_PRIVACY.DPA} prefetch={false}>
                  Data Processing Agreement
                </Link>{' '}
                or in a superseding written agreement between a Client and CoSupport.
              </p>
              <br />
              <p>
                <b>Third Parties</b>
              </p>
              <p>
                We currently use third party Sub-processors. Prior to engaging any third party
                Sub-processors, we perform due diligence to evaluate their privacy, security and
                confidentiality practices, and execute an agreement with them that implements their
                obligations. Please note, that this is a complete list of the Sub-processors we may
                use for the service provision. If you would like to receive the exact list of
                Sub-processors we use in your case, please contact our Data Protection Officer at
                the addresses specified below.
              </p>
              <table>
                <thead>
                  <tr>
                    <th scope="col">
                      <b>Entity Name</b>
                    </th>
                    <th scope="col">
                      <b>Sub-processing Activities</b>
                    </th>
                    <th scope="col">
                      <b>Location</b>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Google LLC</td>
                    <td>Cloud Service Provider</td>
                    <td>USA, Europe</td>
                  </tr>
                  <tr>
                    <td>DigitalOcean, LLC</td>
                    <td>Customer Support and Interaction</td>
                    <td>The Netherlands</td>
                  </tr>
                  <tr>
                    <td>OpenAI OpCo, LLC</td>
                    <td>Provider of generative artificial intelligence features</td>
                    <td>USA</td>
                  </tr>
                </tbody>
              </table>
              <p>
                <b>Updates</b>
              </p>
              <p>
                As our business grows and evolves, the Sub-processors we engage may also change. We
                undertake to timely update this list. You can sign up to receive email notifications
                of any such changes.
              </p>
              <NotifyContainer>
                <NotifyWrapper>
                  <EmailContainer>
                    <label htmlFor="email">Your email:</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="my-email@company.com"
                      data-placeholder="my-email@company.com"
                      className={emailErrorText ? (isEmailValid ? 'valid' : 'invalid') : ''}
                      onChange={(event) => setEmail(event.currentTarget.value)}
                      onBlur={onBlurHandler}
                    />
                    <div
                      className={
                        emailErrorText
                          ? isEmailValid
                            ? 'tooltip hidden'
                            : 'tooltip'
                          : 'tooltip hidden'
                      }
                    >
                      {emailErrorText}
                    </div>
                  </EmailContainer>
                  <GreenButton disabled={!email} onClick={submitForm} className="submit-button">
                    <span>Notify me</span>
                  </GreenButton>
                  <SuccessMessage>{isSuccess && 'Success!'}</SuccessMessage>
                </NotifyWrapper>
              </NotifyContainer>
              <p>
                If you have any questions concerning data processing by us please contact our Data
                Protection Officer by email at:{' '}
                <a href="mailto:contact@cosupports.ai">contact@cosupports.ai</a>.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
export const getStaticProps = async (
  context: GetServerSidePropsContext
): Promise<{ props: SubProcessorsListPageProps }> => {
  const originUrl: string = process.env.ORIGIN_URL || 'https://cosupport.ai';

  return {
    props: { originUrl },
  };
};
